'use client';

import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import ContactModal from './index';
import ContactDrawer from './ContactDrawer';
import { useIsMobile } from './useIsMobile';
import { useScrollLock } from './ScrollLockContext';

type ContactModalContextValue = {
  openModal: () => void;
  closeModal: () => void;
};

const ContactModalContext = createContext<ContactModalContextValue | null>(null);

export function ContactModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const { lock, unlock } = useScrollLock();
  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (isOpen) lock();
    else unlock();
    return () => unlock();
  }, [isOpen, lock, unlock]);

  return (
    <ContactModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      {isMobile ? (
        <ContactDrawer isOpen={isOpen} onClose={closeModal} />
      ) : (
        <ContactModal isOpen={isOpen} onClose={closeModal} />
      )}
    </ContactModalContext.Provider>
  );
}

export function useContactModal() {
  const ctx = useContext(ContactModalContext);
  if (!ctx) {
    throw new Error('useContactModal must be used within ContactModalProvider');
  }
  return ctx;
}
